/**
 * Transforms video elements in HTML content to optimize preload behavior
 * @param {string} content - HTML content to transform
 * @returns {string|null} Transformed HTML content or null
 */
export function contentTransformations(content, isAdvertisingConsentGranted) {
    if (!content) {
        return null;
    }

    let text = content;
    const regex = /<video[^>]*?(?:\b(preload)\s*=\s*"(metadata)"[^>]*?)(?:\bdata-video-(width|height)\s*=\s*"(\d+)"[^>]*?)(?:\bdata-video-(width|height)\s*=\s*"(\d+)"[^>]*?)>.*?<\/video>/g;
    const match = content.match(regex);

    if (match) {
        // https://dev.to/shivampawar/fix-replaceall-is-not-a-function-in-javascript-3klp
        text = text.replace(/'preload="none"'/g, 'preload="metadata"');
        match.forEach((element) => {
            const newElement = element.replace(/'preload="metadata"'/g, 'preload="none"');
            text = text.replace(element, newElement);
        });
    }

    if (!isAdvertisingConsentGranted) {
        text.replace(/(?:www\.)?youtube-nocookie\.com/gi, 'www.youtube.com');
    }

    return text;
}

/**
 * Attaches required attributes to iframes and wraps them in a container
 * @param {Element} contentElement - DOM element containing iframes
 */
export function attachIframeWrapper(contentElement) {
    if (!contentElement) {
        return;
    }

    const iframes = contentElement.querySelectorAll('iframe');

    iframes.forEach((iframe) => {
        iframe.setAttribute('height', '100%');
        iframe.setAttribute('width', '100%');
        iframe.setAttribute('allowfullscreen', 'true');
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

        const parent = iframe.parentNode;
        if (parent && !parent.hasAttribute('data-video')) {
            parent.setAttribute('data-video', 'embed');
        }
    });
}

/**
 * Creates and attaches an IntersectionObserver to autoplay videos when visible
 * @param {Element} contentElement - DOM element containing videos
 * @returns {IntersectionObserver|null} The created observer or null
 */
export function attachVideoObserver(contentElement) {
    if (!contentElement) {
        return null;
    }

    const videos = contentElement.querySelectorAll('video');

    if (videos.length === 0) {
        return null;
    }

    const observer = new IntersectionObserver((entries, observerInstance) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                entry.target.play();
                observerInstance.unobserve(entry.target);
            }
        });
    });

    videos.forEach((video) => {
        observer.observe(video);
    });

    return observer;
}

/**
 * Checks if an iframe source URL is from YouTube or Vimeo
 * @param {string} src - The iframe src URL
 * @returns {boolean} True if the iframe is from YouTube or Vimeo
 */
function isVideoEmbedIframe(src) {
    if (!src) {
        return false;
    }

    const videoProviders = [
        'youtube.com',
        'youtu.be',
        'vimeo.com',
    ];

    const srcLower = src.toLowerCase();
    return videoProviders.some(provider => srcLower.includes(provider));
}

/**
 * Gets iframe source from various possible attributes
 * @param {Element} iframe - The iframe element
 * @returns {string|null} The iframe source URL
 */
function getIframeSrc(iframe) {
    return iframe.getAttribute('src')
        || iframe.getAttribute('data-src')
        || iframe.dataset.src
        || null;
}

/**
 * Generates HTML for privacy placeholder message
 * @returns {string} HTML string for privacy placeholder
 */
function generatePrivacyPlaceholderHTML() {
    return `
        <div class="_aspect-video _flexcol _gap-3 _jc-c _ai-c _rounded--common _bc--light _pa-3 _ba-1">
            <button type="button" class="_tc--accent fa fa-ban _fa--2x" aria-hidden="true"></button>
            <div class="_tac">
                <h4 role="heading" aria-level="4" class="gfu-hd gfu-hd--h4">
                    Your privacy settings are blocking this content
                </h4>
                <div>
                    To view it, please allow third-party content in your privacy settings.
                </div>
            </div>
            <button
                type="button"
                class="gfu-btn gfu-btn--primary gfu-btn--soft"
                data-click="manage-privacy-setting">
                Manage privacy settings
            </button>
        </div>
    `;
}

/**
 * Replaces iframes with privacy blocking UI when third-party content is blocked
 * Excludes YouTube and Vimeo iframes from replacement
 * @param {string} content - HTML content containing iframes
 * @returns {string} Transformed HTML with privacy placeholders
 */
export function replaceIframesWithPrivacyPlaceholder(content) {
    if (!content) {
        return '';
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    doc.querySelectorAll('iframe').forEach((iframe) => {
        const src = getIframeSrc(iframe);

        if (isVideoEmbedIframe(src)) {
            return;
        }

        const replacement = doc.createElement('div');
        replacement.innerHTML = generatePrivacyPlaceholderHTML();

        iframe.parentNode.replaceWith(replacement);
    });

    return doc.body.innerHTML;
}
